/**
 * User Management API Service
 *
 * Provides API functions for user and entitlement management
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 * Pattern: Method-specific endpoints with proper parameter handling
 */

import { API_ENDPOINTS, buildEndpoint, getApiBaseUrl } from './endpoints';

// Team invite uses TEAM.INVITE from endpoints.ts
// Backend endpoint pending implementation in template-core.yaml

// Type Definitions
export interface User {
  Email_Address: string;
  Client_ID: string;
  User_Display_Name?: string;
  First_Name?: string;
  Last_Name?: string;
  Super_Admin: boolean;
  User_Status: string;
  Create_Date: string;
  Last_Login?: string;
}

export interface UserEntitlement {
  Email_Address: string;
  Client_ID: string;
  Company_ID: string;
  Admin: boolean;
  Approver: boolean;
  Executive: boolean;
  Granted_At: string;
  Expires_At?: string;
  Client_Name?: string;
  Client_Type?: string;
  Company_Name?: string;
}

export interface CreateUserRequest {
  Email_Address: string;
  Client_ID: string;
  User_Display_Name?: string;
  First_Name?: string;
  Last_Name?: string;
  User_Status?: string;
}

export interface UpdateUserRequest {
  Email_Address: string;
  User_Display_Name?: string;
  First_Name?: string;
  Last_Name?: string;
  User_Status?: string;
}

export interface UpdateEntitlementsRequest {
  Email_Address: string;
  Client_ID: string;
  Company_ID: string;
  Admin?: boolean;
  Approver?: boolean;
  Executive?: boolean;
}

export interface APIResponse<T> {
  success: boolean;
  message: string;
  data: {
    Records: T[];
  };
  meta: {
    Total_Records: number;
    Request_ID: string;
    Timestamp: string;
  };
}

// API Client Helper
async function makeAuthorizedAPICall<T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: any,
  queryParams?: Record<string, string>
): Promise<APIResponse<T>> {
  // Get ID token from localStorage (Cognito authorizer expects ID token, not access token)
  const idToken = localStorage.getItem('accessToken');

  if (!idToken) {
    throw new Error('No ID token found. Please log in.');
  }

  // Build full URL with base API URL
  const baseUrl = getApiBaseUrl();
  let url = `${baseUrl}${endpoint}`;
  if (queryParams && (method === 'GET' || method === 'DELETE')) {
    const params = new URLSearchParams(queryParams);
    url = `${baseUrl}${endpoint}?${params.toString()}`;
  }

  const options: RequestInit = {
    method,
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
  };

  // Add body for POST/PUT
  if (body && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API call failed: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get user by email address
 *
 * GET /tim/users?Email_Address=user@example.com
 */
export async function getUser(Email_Address: string): Promise<APIResponse<User>> {
  return makeAuthorizedAPICall<User>(
    API_ENDPOINTS.USERS.GET,
    'GET',
    undefined,
    { Email_Address }
  );
}

/**
 * Get all users for a client
 *
 * GET /tim/users?Company_ID=CL-001
 */
export async function getUsersByClient(Client_ID: string): Promise<APIResponse<User>> {
  return makeAuthorizedAPICall<User>(
    API_ENDPOINTS.USERS.LIST,
    'GET',
    undefined,
    { Company_ID: Client_ID }
  );
}

/**
 * Get all users (super admin only)
 *
 * GET /tim/users
 */
export async function getAllUsers(): Promise<APIResponse<User>> {
  return makeAuthorizedAPICall<User>(
    API_ENDPOINTS.USERS.LIST,
    'GET'
  );
}

/**
 * Create new user
 *
 * POST /tim/users
 */
export async function createUser(userData: CreateUserRequest): Promise<APIResponse<User>> {
  return makeAuthorizedAPICall<User>(
    API_ENDPOINTS.USERS.CREATE,
    'POST',
    userData
  );
}

/**
 * Update existing user
 *
 * PUT /tim/users
 */
export async function updateUser(userData: UpdateUserRequest): Promise<APIResponse<User>> {
  return makeAuthorizedAPICall<User>(
    API_ENDPOINTS.USERS.UPDATE,
    'PUT',
    userData
  );
}

/**
 * Delete (deactivate) user
 *
 * DELETE /tim/users?Email_Address=user@example.com
 */
export async function deleteUser(Email_Address: string): Promise<APIResponse<User>> {
  return makeAuthorizedAPICall<User>(
    API_ENDPOINTS.USERS.DELETE,
    'DELETE',
    undefined,
    { Email_Address }
  );
}

/**
 * Get user entitlements
 *
 * GET /tim/entitlements?Email_Address=user@example.com
 */
export async function getUserEntitlements(
  Email_Address: string,
  Client_ID?: string
): Promise<APIResponse<UserEntitlement>> {
  const params: Record<string, string> = { Email_Address };
  if (Client_ID) {
    params.Client_ID = Client_ID;
  }

  return makeAuthorizedAPICall<UserEntitlement>(
    API_ENDPOINTS.ENTITLEMENTS.LIST,
    'GET',
    undefined,
    params
  );
}

/**
 * Create user entitlement
 *
 * POST /tim/entitlements
 */
export async function createUserEntitlement(
  entitlementData: UpdateEntitlementsRequest
): Promise<APIResponse<UserEntitlement>> {
  return makeAuthorizedAPICall<UserEntitlement>(
    API_ENDPOINTS.ENTITLEMENTS.CREATE,
    'POST',
    entitlementData
  );
}

/**
 * Update user entitlements
 *
 * PUT /tim/entitlements
 */
export async function updateUserEntitlements(
  entitlementData: UpdateEntitlementsRequest
): Promise<APIResponse<UserEntitlement>> {
  return makeAuthorizedAPICall<UserEntitlement>(
    API_ENDPOINTS.ENTITLEMENTS.UPDATE,
    'PUT',
    entitlementData
  );
}

/**
 * Delete user entitlement
 *
 * DELETE /tim/entitlements?Email_Address=user@example.com&Client_ID=CL-001&Company_ID=CO-001
 */
export async function deleteUserEntitlement(
  Email_Address: string,
  Client_ID: string,
  Company_ID: string
): Promise<APIResponse<UserEntitlement>> {
  return makeAuthorizedAPICall<UserEntitlement>(
    API_ENDPOINTS.ENTITLEMENTS.DELETE,
    'DELETE',
    undefined,
    { Email_Address, Client_ID, Company_ID }
  );
}

// Helper function to check if response is successful
export function isSuccessResponse<T>(response: APIResponse<T>): boolean {
  return response.success === true;
}

/**
 * Team Invitation Types
 */
export interface TeamInvitation {
  Invitation_ID: string;
  Email_Address: string;
  Client_ID: string;
  Company_ID?: string;
  Invited_By: string;
  Invitation_Status: 'pending' | 'sent' | 'accepted' | 'expired' | 'resent' | 'failed';
  Invitation_Token: string;
  Sent_At?: string;
  Expires_At: string;
  Accepted_At?: string;
  Created_At: string;
}

export interface SendInvitationRequest {
  Email_Address: string;
  Client_ID: string;
  Company_ID?: string;
  Invited_By_Email: string;
}

/**
 * Send team invitation email
 *
 * POST /api/admin/team-invite
 */
export async function sendTeamInvitation(
  invitationData: SendInvitationRequest
): Promise<APIResponse<TeamInvitation>> {
  return makeAuthorizedAPICall<TeamInvitation>(
    API_ENDPOINTS.TEAM.INVITE,
    'POST',
    invitationData
  );
}

/**
 * Resend team invitation email
 *
 * POST /api/admin/team-invite/resend
 */
export async function resendTeamInvitation(
  Email_Address: string,
  Client_ID: string,
  Invited_By_Email: string
): Promise<APIResponse<TeamInvitation>> {
  return makeAuthorizedAPICall<TeamInvitation>(
    API_ENDPOINTS.TEAM.INVITE,
    'POST',
    { Email_Address, Client_ID, Invited_By_Email, resend: true }
  );
}

/**
 * Get invitation status for a user
 *
 * GET /api/admin/team-invite?Email_Address=user@example.com&Client_ID=CL-001
 */
export async function getInvitationStatus(
  Email_Address: string,
  Client_ID: string
): Promise<APIResponse<TeamInvitation>> {
  return makeAuthorizedAPICall<TeamInvitation>(
    API_ENDPOINTS.TEAM.INVITE,
    'GET',
    undefined,
    { Email_Address, Client_ID }
  );
}
